import { ThemeName } from '@packages/types'
import { useSelector, useDispatch } from 'react-redux'

import * as twoDDisplayerActions from 'customizer/2dDisplayer/actions'
import * as customizationActions from 'customizer/customization/actions'
import * as customizationSelectors from 'customizer/customization/selectors'
import { createColorAnswer, updateColorAnswer } from 'customizer/customization/actions'
import * as pricingSelectors from 'customizer/pricing/selectors'
import * as questionPanelActions from 'customizer/questionPanel/actions'
import { disablePersonalisationsSelector } from 'customizer/questionPanel/selectors'
import * as stocksSelectors from 'customizer/stocks/selectors'
import * as themeSelectors from 'customizer/theme/selectors'

const { currencySelector, customizationQuestionPriceSelector } = pricingSelectors

const {
  selectAnswer,
  selectAnswers,
  removeAnswer,
  createTextAnswer,
  updateText,
  createLogoAnswer,
  createAssetAnswer,
  setQuestionHasRequiredError,
} = customizationActions

const { firstLevelAncestorSelector } = customizationSelectors

const { highlightStep } = questionPanelActions
const { themeConfigSelector } = themeSelectors

const useQuestionProps = question => {
  const dispatch = useDispatch()
  const currency = useSelector(currencySelector)
  const firstLevelAncestorStep = useSelector(state => firstLevelAncestorSelector(state, question.id))
  const lang = useSelector(state => state.lang)
  const country = useSelector(state => state.country)
  const disablePersonalisations = useSelector(disablePersonalisationsSelector)
  const answersOutOfStock = useSelector(state =>
    stocksSelectors.answersOutOfStockForQuestionSelector(state, question.id)
  )

  const pricing = useSelector(state => customizationQuestionPriceSelector(state, question))

  const themeConfig = useSelector(state =>
    themeConfigSelector(state, { themeName: ThemeName.Booklike, id: question.id })
  )

  const handleSelectAnswer = answerId => {
    dispatch(selectAnswer(question.id, answerId))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleSelectAnswers = answerIds => {
    dispatch(selectAnswers(question.id, answerIds))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleRemoveAnswer = answerId => {
    dispatch(removeAnswer(question.id, answerId))
  }

  const handleUpdateTextAnswer = (questionId, answerId, text, productViewText) => {
    dispatch(updateText(questionId, answerId, text, productViewText))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleCreateTextAnswer = (questionId, text, productViewText) => {
    dispatch(createTextAnswer(questionId, text, productViewText))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleCreateLogoAnswer = (questionId, asset) => {
    dispatch(createLogoAnswer(questionId, asset))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleCreateAssetAnswer = (questionId, value) => {
    dispatch(createAssetAnswer(questionId, value))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleFocusQuestion = () => {
    firstLevelAncestorStep.forceView != null &&
      dispatch(twoDDisplayerActions.switchView(firstLevelAncestorStep.forceView))
    dispatch(highlightStep(question))
  }

  const handleCreateColorAnswer = (questionId, color) => {
    dispatch(createColorAnswer(questionId, color))
    dispatch(setQuestionHasRequiredError(question.id, false))
  }

  const handleUpdateColorAnswer = (questionId, answerId, color) => {
    dispatch(updateColorAnswer(questionId, answerId, color))
    dispatch(setQuestionHasRequiredError(question.id, answerId, color))
  }

  return {
    currency,
    lang,
    country,
    pricing,
    answersOutOfStock,
    handleSelectAnswer,
    handleSelectAnswers,
    handleRemoveAnswer,
    handleUpdateTextAnswer,
    handleCreateTextAnswer,
    handleCreateLogoAnswer,
    handleCreateAssetAnswer,
    handleFocusQuestion,
    themeConfig,
    disablePersonalisations,
    handleCreateColorAnswer,
    handleUpdateColorAnswer,
  }
}

export default useQuestionProps
